import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';

import { DrawerModule } from 'primeng/drawer';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { MessageService } from 'primeng/api';

import { EnumService } from '@shared/api/services/enum.service';
import { AuthService } from '@auth/services/auth.service';
import { OcorrenciaService, OcorrenciaAnexoService } from '@core/services/domain-services';
import { EncerrarOcorrenciaPayload } from '@core/services/domain-services';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { OPCOES_DESFECHO_FALLBACK } from '../ocorrencia-resolution.model';

interface OcorrenciaAEncerrar {
  id: number;
  dataOcorrencia: string;
  tipoOcorrencia?: number | string | null;
  estadoEnum?: number | null;
  criticidade?: any;
  estado?: string | null;
}

interface AnexoPreview {
  file: File;
  preview: string;
}

@Component({
  selector: 'app-fechar-ocorrencia',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DrawerModule,
    DatePickerModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    ButtonModule,
    FileUploadModule,
    TagModule,
  ],
  templateUrl: './fechar-ocorrencia.component.html',
  styleUrls: ['./fechar-ocorrencia.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FecharOcorrenciaComponent implements OnChanges {
  @Input() visible = false;
  @Input() ocorrencia: OcorrenciaAEncerrar | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() fechada = new EventEmitter<number>();

  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly enumService = inject(EnumService);
  private readonly authService = inject(AuthService);
  private readonly ocorrenciaService = inject(OcorrenciaService);
  private readonly ocorrenciaAnexoService = inject(OcorrenciaAnexoService);
  private readonly messageService = inject(MessageService);

  form: FormGroup = this.criarFormulario();
  opcoesDesfecho: Array<{ label: string; value: number }> = OPCOES_DESFECHO_FALLBACK;
  resolvidoPor = '';
  minDateResolucao: Date | null = null;
  anexos: AnexoPreview[] = [];
  isSaving = false;

  entidadesOpcoes = ['Polícia', 'Bombeiros', 'Cliente', 'Outra'];
  entidadesSeleccionadas: string[] = [];
  temOutraEntidade = false;
  outraEntidade = '';

  temDanosPerdas = false;

  private readonly tipoLabelMap: Record<string, string> = {
    'INTRUSAO': 'Intrusão',
    'AUSENCIA': 'Ausência',
    'AVARIA': 'Avaria',
    'RONDA_INCOMPLETA': 'Ronda Incompleta',
    'SUSPEITO': 'Suspeito',
    'ACIDENTE': 'Acidente',
    'OUTRO': 'Outro',
    'INCIDENTE': 'Incidente',
    'FALHA_EQUIPAMENTO': 'Falha de Equipamento',
    'EMERGENCIA': 'Emergência',
    '1': 'Incidente',
    '2': 'Falha de Equipamento',
    '3': 'Intrusão',
    '4': 'Emergência',
    '5': 'Outro',
    '6': 'Falha de Equipamento',
    '7': 'Outro',
  };

  private readonly criticidadeLabelMap: Record<string, string> = {
    '1': 'Baixa', 'BAIXA': 'Baixa',
    '2': 'Média', 'MEDIA': 'Média',
    '3': 'Alta', 'ALTA': 'Alta',
    '4': 'Urgente', 'URGENTE': 'Urgente',
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ocorrencia'] || changes['visible']) {
      this.inicializarFormulario();
    }
    if (changes['visible'] && this.visible) {
      this.carregarDesfecho();
      this.resolvidoPor = this.authService.getUserName?.() || (this.authService as any).getUser?.()?.nome || '';
    }
  }

  private criarFormulario(): FormGroup {
    return this.fb.group({
      desfecho: [null, [Validators.required]],
      acoesTomadas: ['', [Validators.required, Validators.minLength(5)]],
      dataResolucao: [new Date(), [Validators.required]],
      entidadesAccionadas: [''],
      valorDanosPerdas: [null, [Validators.min(0)]],
      descricaoDanosPerdas: [''],
    });
  }

  private inicializarFormulario(): void {
    this.form = this.criarFormulario();
    this.anexos = [];
    this.entidadesSeleccionadas = [];
    this.temOutraEntidade = false;
    this.outraEntidade = '';
    this.temDanosPerdas = false;
    this.minDateResolucao = null;
    if (this.ocorrencia?.dataOcorrencia) {
      const data = new Date(this.ocorrencia.dataOcorrencia);
      if (!isNaN(data.getTime())) {
        this.minDateResolucao = data;
        const atual = this.form.get('dataResolucao')?.value as Date | null;
        if (!atual || atual < data) {
          this.form.get('dataResolucao')?.setValue(new Date());
        }
      }
    }
    this.cdr.markForCheck();
  }

  private carregarDesfecho(): void {
    this.enumService
      .getEnum('DesfechoOcorrencia')
      .pipe(
        catchError(() => of({} as Record<string, string>)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((itens) => {
        const opcoes = Object.entries(itens ?? {})
          .map(([key, label]) => ({ label, value: Number(key) }))
          .filter((op) => !isNaN(op.value));
        this.opcoesDesfecho = opcoes.length ? opcoes : OPCOES_DESFECHO_FALLBACK;
        this.cdr.markForCheck();
      });
  }

  toggleEntidade(entidade: string): void {
    if (entidade === 'Outra') {
      this.temOutraEntidade = !this.temOutraEntidade;
      if (!this.temOutraEntidade) this.outraEntidade = '';
      this.atualizarEntidadesAccionadas();
      this.cdr.markForCheck();
      return;
    }
    const idx = this.entidadesSeleccionadas.indexOf(entidade);
    if (idx >= 0) this.entidadesSeleccionadas.splice(idx, 1);
    else this.entidadesSeleccionadas.push(entidade);
    this.atualizarEntidadesAccionadas();
    this.cdr.markForCheck();
  }

  isEntidadeSeleccionada(entidade: string): boolean {
    if (entidade === 'Outra') return this.temOutraEntidade;
    return this.entidadesSeleccionadas.includes(entidade);
  }

  onOutraEntidadeChange(): void {
    this.atualizarEntidadesAccionadas();
    this.cdr.markForCheck();
  }

  private atualizarEntidadesAccionadas(): void {
    const partes: string[] = [...this.entidadesSeleccionadas];
    if (this.temOutraEntidade && this.outraEntidade?.trim()) {
      partes.push(this.outraEntidade.trim());
    }
    this.form.get('entidadesAccionadas')?.setValue(partes.length ? partes.join(', ') : '');
  }

  toggleDanosPerdas(valor: boolean): void {
    this.temDanosPerdas = valor;
    if (!valor) {
      this.form.get('valorDanosPerdas')?.setValue(null);
      this.form.get('descricaoDanosPerdas')?.setValue('');
    }
    this.cdr.markForCheck();
  }

  getTipoLabel(v: any): string {
    const chave = String(v ?? '').toUpperCase();
    if (this.tipoLabelMap[chave]) return this.tipoLabelMap[chave];
    const chaveNum = String(v ?? '');
    if (this.tipoLabelMap[chaveNum]) return this.tipoLabelMap[chaveNum];
    return String(v ?? '');
  }

  getCriticidadeLabel(v: any): string {
    const chave = String(v ?? '').toUpperCase();
    if (this.criticidadeLabelMap[chave]) return this.criticidadeLabelMap[chave];
    const chaveNum = String(v ?? '');
    if (this.criticidadeLabelMap[chaveNum]) return this.criticidadeLabelMap[chaveNum];
    return String(v ?? '');
  }

  getEstadoLabel(v: any): string {
    const estadoEnum = this.ocorrencia?.estadoEnum;
    if (estadoEnum === 1) return 'Aberta';
    if (estadoEnum === 2) return 'Fechada';
    if (estadoEnum === 3) return 'Em Progresso';
    const chave = String(v ?? '').toUpperCase();
    if (chave === '1' || chave === 'ABERTA') return 'Aberta';
    if (chave === '2' || chave === 'FECHADA') return 'Fechada';
    if (chave === '3' || chave === 'EM_PROGRESSO' || chave === 'EM PROGRESSO') return 'Em Progresso';
    return String(v ?? '');
  }

  getCriticidadeSeverity(v: any): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const chave = String(v ?? '').toUpperCase();
    if (chave === '4' || chave === 'URGENTE') return 'danger';
    if (chave === '3' || chave === 'ALTA') return 'warn';
    if (chave === '2' || chave === 'MEDIA') return 'info';
    return 'secondary';
  }

  getEstadoSeverity(v: any): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const estadoEnum = this.ocorrencia?.estadoEnum;
    if (estadoEnum === 1) return 'warn';
    if (estadoEnum === 2) return 'secondary';
    if (estadoEnum === 3) return 'info';
    const chave = String(v ?? '').toUpperCase();
    if (chave === '1' || chave === 'ABERTA') return 'warn';
    if (chave === '2' || chave === 'FECHADA') return 'secondary';
    if (chave === '3' || chave === 'EM_PROGRESSO') return 'info';
    return 'secondary';
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;
    Array.from(files).forEach((file) => {
      compressImageIfNeeded(file)
        .then((processed) => {
          const reader = new FileReader();
          reader.onload = () => {
            this.anexos.push({ file: processed, preview: String(reader.result) });
            this.cdr.markForCheck();
          };
          reader.readAsDataURL(processed);
        })
        .catch(() => {
          const reader = new FileReader();
          reader.onload = () => {
            this.anexos.push({ file, preview: String(reader.result) });
            this.cdr.markForCheck();
          };
          reader.readAsDataURL(file);
        });
    });
    input.value = '';
  }

  removerAnexo(index: number): void {
    this.anexos.splice(index, 1);
    this.cdr.markForCheck();
  }

  fecharDrawer(): void {
    if (this.isSaving) return;
    this.visible = false;
    this.visibleChange.emit(false);
  }

  guardar(): void {
    if (!this.ocorrencia?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível identificar a ocorrência a fechar.',
      });
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulário inválido',
        detail: 'Preencha os campos obrigatórios e verifique as validações.',
      });
      return;
    }
    const dataResolucao = this.form.get('dataResolucao')?.value as Date | null;
    if (!dataResolucao) return;
    if (this.minDateResolucao && dataResolucao < this.minDateResolucao) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Data inválida',
        detail: 'A data de resolução não pode ser anterior à data da ocorrência.',
      });
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const entidadesAccionadas = this.limparTexto(this.form.get('entidadesAccionadas')?.value);
    const descricaoDanosPerdas = this.temDanosPerdas ? this.limparTexto(this.form.get('descricaoDanosPerdas')?.value) : null;
    const acoesTomadas = String(this.form.get('acoesTomadas')?.value || '').trim();
    const desfecho = Number(this.form.get('desfecho')?.value);
    const valorDanosPerdasRaw = this.temDanosPerdas ? this.form.get('valorDanosPerdas')?.value : null;
    const valorDanosPerdas =
      valorDanosPerdasRaw === null || valorDanosPerdasRaw === undefined || valorDanosPerdasRaw === ''
        ? null
        : Number(valorDanosPerdasRaw);

    const payload: EncerrarOcorrenciaPayload = {
      desfecho,
      acoesTomadas,
      dataResolucao: dataResolucao.toISOString(),
      entidadesAccionadas,
      valorDanosPerdas: valorDanosPerdas === null ? null : isNaN(valorDanosPerdas) ? null : valorDanosPerdas,
      descricaoDanosPerdas,
    };

    this.ocorrenciaService
      .encerrar(this.ocorrencia.id, payload)
      .pipe(
        switchMap(() => {
          if (!this.anexos.length) return of(null);
          const uploads = this.anexos.map((anexo) => {
            const formData = new FormData();
            formData.append('file', anexo.file);
            return this.ocorrenciaAnexoService.create(formData as any).pipe(
              catchError(() => of(null))
            );
          });
          return forkJoin(uploads);
        }),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Ocorrência fechada',
            detail: 'A ocorrência foi fechada com sucesso.',
          });
          this.fechada.emit(this.ocorrencia!.id);
          this.visible = false;
          this.visibleChange.emit(false);
          this.anexos = [];
        },
        error: (err) => {
          const mensagem =
            err?.error?.retorno?.mensagem ||
            err?.error?.message ||
            err?.message ||
            'Não foi possível fechar a ocorrência.';
          this.messageService.add({
            severity: 'error',
            summary: 'Erro ao fechar ocorrência',
            detail: mensagem,
          });
        },
      });
  }

  private limparTexto(valor: unknown): string | null {
    if (valor === null || valor === undefined) return null;
    const texto = String(valor).trim();
    return texto === '' ? null : texto;
  }

  temErro(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }
}
